import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { paymentsService } from '../services/supabase';
import { Payment } from '../types';
import { PaymentRevenueData } from '../components/charts/PaymentRevenueChart';
import { getRevenueAmount } from '../utils/paymentRevenue';

interface UsePaymentRevenueDataOptions {
  monthsToShow?: number; // Number of months to display (year/total modes)
  mode?: 'month' | 'year' | 'total'; // Aggregation mode
  payments?: Payment[]; // Optional: reuse already loaded payments to avoid duplicate fetch
}

interface UsePaymentRevenueDataReturn {
  data: PaymentRevenueData[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const usePaymentRevenueData = (options: UsePaymentRevenueDataOptions = {}): UsePaymentRevenueDataReturn => {
  const { monthsToShow = 15, mode = 'year', payments: providedPayments } = options;
  const hasProvided = providedPayments !== undefined;
  const [fetchedPayments, setFetchedPayments] = useState<Payment[] | null>(null);
  const [loading, setLoading] = useState(!hasProvided);
  const [error, setError] = useState<string | null>(null);

  const data = useMemo(() => {
    const source = providedPayments ?? fetchedPayments ?? [];
    return computeRevenueData(source, mode, monthsToShow);
  }, [providedPayments, fetchedPayments, mode, monthsToShow]);

  const fetchDataRef = useRef<() => Promise<void>>();

  fetchDataRef.current = useCallback(async () => {
    if (hasProvided) return;
    try {
      setLoading(true);
      setError(null);
      const payments = await paymentsService.getAll();
      setFetchedPayments(payments);
    } catch (err) {
      console.error('Error fetching payment revenue data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [hasProvided]);

  useEffect(() => {
    if (hasProvided) {
      setLoading(false);
      return;
    }
    fetchDataRef.current?.();
  }, [hasProvided, monthsToShow, mode]);

  const refresh = useCallback(async () => {
    await fetchDataRef.current?.();
  }, []);

  return { data, loading, error, refresh };
};

function computeRevenueData(
  payments: Payment[],
  mode: 'month' | 'year' | 'total',
  monthsToShow: number
): PaymentRevenueData[] {
  if (mode === 'month') return generateWeeksWindowData(payments);
  if (mode === 'total') return generateYearsWindowData(payments);
  return generateMonthsData(payments, monthsToShow);
}

/**
 * Generate revenue data grouped by month
 * Shows 11 months before current month + current month + 3 months after
 */
function generateMonthsData(payments: Payment[], monthsToShow: number): PaymentRevenueData[] {
  const now = new Date();
  const currentMonthKey = getMonthKey(now);
  const monthsMap = new Map<string, PaymentRevenueData>();

  // Initialize months: 11 before + current + 3 after = 15 months total
  // Start from 11 months ago
  for (let i = -11; i <= 3; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthKey = getMonthKey(date);
    const monthLabel = getMonthLabel(date);
    
    monthsMap.set(monthKey, {
      month: monthLabel,
      paid: 0,
      overdue: 0,
      pending: 0,
      total: 0, // Somme totale pour l'arrondi global
      isCurrentMonth: monthKey === currentMonthKey,
      monthType: i < 0 ? 'past' : i === 0 ? 'current' : 'future'
    });
  }

  // Group payments by month and status
  // Note: Uses amountBaseCurrency (in user's base currency) for consistent statistics across all currencies
  payments.forEach(payment => {
    // Use the appropriate date based on status:
    // - For paid payments, use payment date
    // - For pending/overdue payments, use due date (when payment is expected)
    const relevantDate = payment.status === 'paid' && payment.paymentDate
      ? new Date(payment.paymentDate)
      : new Date(payment.dueDate);
    
    const monthKey = getMonthKey(relevantDate);
    
    // Only include payments from the months we're showing
    if (monthsMap.has(monthKey)) {
      const monthData = monthsMap.get(monthKey)!;
      
      // Add to the appropriate category based on status
      // Use amountBaseCurrency (in user's base currency) for consistent statistics
      const amount = getRevenueAmount(payment);
      if (payment.status === 'paid') {
        monthData.paid += amount;
      } else if (payment.status === 'overdue') {
        monthData.overdue += amount;
      } else if (payment.status === 'sent') {
        monthData.pending += amount;
      }
    }
  });

  // Calculate total for each month (once after all payments are processed)
  monthsMap.forEach((monthData) => {
    monthData.total = monthData.paid + monthData.pending + monthData.overdue;
  });

  // Convert map to array, maintaining chronological order
  return Array.from(monthsMap.values());
}

// Generate weekly window: 4 weeks before current week, current week, 4 weeks after (9 entries)
function generateWeeksWindowData(payments: Payment[]): PaymentRevenueData[] {
  const now = new Date();
  const startOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 (Sun) - 6 (Sat)
    const diff = (day + 6) % 7; // set Monday as start
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

  const currentWeekStart = startOfWeek(now);
  const weeks: { key: string; label: string; start: Date; end: Date; type: 'past'|'current'|'future' }[] = [];
  for (let i = -11; i <= 3; i++) {
    const start = addDays(currentWeekStart, i * 7);
    const end = addDays(start, 6);
    // Standard SaaS: Use ISO week year (not calendar year) for correct week labeling
    // ISO week 1 can start in December of the previous year (e.g., 2026-W01 starts Dec 29, 2025)
    const { weekNumber, isoYear } = getISOWeekNumberAndYear(start);
    const label = `S${weekNumber} ${isoYear}`;
    const type = i < 0 ? 'past' : i === 0 ? 'current' : 'future';
    weeks.push({ key: label, label, start, end, type });
  }

  const map = new Map<string, PaymentRevenueData>();
  weeks.forEach(w => map.set(w.key, { month: w.label, paid: 0, overdue: 0, pending: 0, total: 0, monthType: w.type }));

  payments.forEach(payment => {
    const relevantDate = payment.status === 'paid' && payment.paymentDate ? new Date(payment.paymentDate) : new Date(payment.dueDate);
    weeks.forEach(w => {
      if (relevantDate >= w.start && relevantDate <= w.end) {
        const entry = map.get(w.key)!;
        const amount = getRevenueAmount(payment);
        if (payment.status === 'paid') entry.paid += amount;
        else if (payment.status === 'overdue') entry.overdue += amount;
        else if (payment.status === 'sent') entry.pending += amount;
      }
    });
  });

  map.forEach(v => { v.total = v.paid + v.pending + v.overdue; });
  
  // Standard SaaS: Sort weeks chronologically by start date to ensure correct order
  // This prevents issues like "S1 2025" appearing after "S52 2024" on small screens
  // We sort by the actual start date timestamp, not by the label, to handle year transitions correctly
  // This ensures weeks are always displayed in chronological order regardless of screen size
  const sortedWeeks = weeks
    .map(w => {
      const data = map.get(w.key);
      if (!data) {
        // Fallback: create empty data entry if somehow missing
        return { week: w, data: { month: w.label, paid: 0, overdue: 0, pending: 0, total: 0, monthType: w.type } };
      }
      return { week: w, data };
    })
    .sort((a, b) => {
      // Sort by start date timestamp for reliable chronological ordering
      // This handles year transitions correctly (S52 2024 < S1 2025)
      const timeDiff = a.week.start.getTime() - b.week.start.getTime();
      if (timeDiff !== 0) {
        return timeDiff;
      }
      // If same start time (shouldn't happen), sort by label as fallback
      return a.week.label.localeCompare(b.week.label);
    })
    .map(item => item.data);
  
  return sortedWeeks;
}

// Generate years window: 11 years back, current, 3 years ahead
function generateYearsWindowData(payments: Payment[]): PaymentRevenueData[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const years: number[] = [];
  for (let y = currentYear - 11; y <= currentYear + 3; y++) years.push(y);

  const map = new Map<number, PaymentRevenueData>();
  years.forEach(y => map.set(y, { month: String(y), paid: 0, overdue: 0, pending: 0, total: 0, monthType: y < currentYear ? 'past' : y === currentYear ? 'current' : 'future' }));

  payments.forEach(payment => {
    const relevantDate = payment.status === 'paid' && payment.paymentDate ? new Date(payment.paymentDate) : new Date(payment.dueDate);
    const y = relevantDate.getFullYear();
    if (map.has(y)) {
      const entry = map.get(y)!;
      const amount = getRevenueAmount(payment);
      if (payment.status === 'paid') entry.paid += amount;
      else if (payment.status === 'overdue') entry.overdue += amount;
      else if (payment.status === 'sent') entry.pending += amount;
    }
  });

  map.forEach(v => { v.total = v.paid + v.pending + v.overdue; });
  return years.map(y => map.get(y)!);
}

// ISO week number helper (Monday-first)
// Returns both week number and ISO year (which may differ from calendar year)
function getISOWeekNumberAndYear(date: Date): { weekNumber: number; isoYear: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Thursday in current week decides the ISO year
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const weekNum = Math.ceil((((d as any) - (yearStart as any)) / 86400000 + 1) / 7);
  return { weekNumber: weekNum, isoYear };
}

/**
 * Get a unique key for a month (YYYY-MM)
 */
function getMonthKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Get a human-readable label for a month
 */
function getMonthLabel(date: Date): string {
  const monthNames = [
    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
    'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
  ];
  
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  
  return `${month} ${year}`;
}
